export type StartNavDir = 'up' | 'down' | 'left' | 'right';

export type StartNavGroup = {
  id: string;
  axis: 'vertical' | 'horizontal';
  kind: 'list' | 'radio';
  items: readonly string[];
};

export type StartNavLayout = {
  /** Group ids per column, left to right. */
  columns: readonly (readonly string[])[];
  groups: readonly StartNavGroup[];
  /** Boat overlay: Left on the first control is back; otherwise stay in the trap. */
  trap: boolean;
};

export type StartNavResult =
  | { type: 'focus'; id: string; activate: boolean }
  | { type: 'back' }
  | { type: 'forward'; id: string };

type Located = {
  group: StartNavGroup;
  groupIndex: number;
  columnIndex: number;
  itemIndex: number;
};

const COLUMN_ORDER = ['main', 'pane', 'maps', 'overlay'] as const;

function alongDelta(axis: StartNavGroup['axis'], dir: StartNavDir): -1 | 0 | 1 {
  if (axis === 'vertical') {
    if (dir === 'up') {
      return -1;
    }

    if (dir === 'down') {
      return 1;
    }

    return 0;
  }

  if (dir === 'left') {
    return -1;
  }

  if (dir === 'right') {
    return 1;
  }

  return 0;
}

function locate(layout: StartNavLayout, id: string): Located | null {
  for (let columnIndex = 0; columnIndex < layout.columns.length; columnIndex += 1) {
    const column = layout.columns[columnIndex];
    if (!column) {
      continue;
    }

    for (const groupId of column) {
      const group = layout.groups.find((entry) => entry.id === groupId);
      if (!group) {
        continue;
      }

      const itemIndex = group.items.indexOf(id);
      if (itemIndex < 0) {
        continue;
      }

      const groupIndex = column.indexOf(groupId);
      return { group, groupIndex, columnIndex, itemIndex };
    }
  }

  return null;
}

function groupInColumn(
  layout: StartNavLayout,
  columnIndex: number,
  groupIndex: number,
): StartNavGroup | null {
  const groupId = layout.columns[columnIndex]?.[groupIndex];
  return groupId
    ? (layout.groups.find((entry) => entry.id === groupId) ?? null)
    : null;
}

function focusItem(
  group: StartNavGroup,
  itemIndex: number,
  activate: boolean,
): StartNavResult | null {
  const id = group.items[itemIndex];
  return id === undefined ? null : { type: 'focus', id, activate };
}

function stepGroup(
  layout: StartNavLayout,
  columnIndex: number,
  groupIndex: number,
  delta: -1 | 1,
): StartNavResult | null {
  const column = layout.columns[columnIndex];
  if (!column) {
    return null;
  }

  const nextGroupIndex = groupIndex + delta;
  if (nextGroupIndex >= 0 && nextGroupIndex < column.length) {
    const group = groupInColumn(layout, columnIndex, nextGroupIndex);
    if (!group || group.items.length === 0) {
      return null;
    }

    return focusItem(group, delta > 0 ? 0 : group.items.length - 1, false);
  }

  // Pane → maps. Main never yields Down into a pane (Right / Enter does that).
  if (delta === 1 && columnIndex > 0) {
    const group = groupInColumn(layout, columnIndex + 1, 0);
    if (group) {
      return focusItem(group, 0, false);
    }
  }

  const wrapIndex = delta > 0 ? 0 : column.length - 1;
  const group = groupInColumn(layout, columnIndex, wrapIndex);
  if (!group || group.items.length === 0) {
    return null;
  }

  return focusItem(group, delta > 0 ? 0 : group.items.length - 1, false);
}

/**
 * Move the splash cursor. `back` / `forward` are for the caller: Escape-style
 * retreat, or activate the focused control.
 */
export function moveStartNav(
  layout: StartNavLayout,
  currentId: string,
  dir: StartNavDir,
): StartNavResult {
  const at = locate(layout, currentId);
  if (!at) {
    const first = layout.groups[0]?.items[0];
    return first
      ? { type: 'focus', id: first, activate: false }
      : { type: 'back' };
  }

  const delta = alongDelta(at.group.axis, dir);

  if (delta !== 0) {
    const nextIndex = at.itemIndex + delta;
    if (nextIndex >= 0 && nextIndex < at.group.items.length) {
      return focusItem(at.group, nextIndex, at.group.kind === 'radio') ?? {
        type: 'back',
      };
    }

    if (layout.trap) {
      const next = groupInColumn(layout, at.columnIndex, at.groupIndex + delta);
      if (next && next.items.length > 0) {
        return (
          focusItem(next, delta > 0 ? 0 : next.items.length - 1, false) ?? {
            type: 'back',
          }
        );
      }

      return delta < 0 ? { type: 'back' } : { type: 'forward', id: currentId };
    }

    if (at.group.axis === 'horizontal') {
      return delta < 0 ? { type: 'back' } : { type: 'forward', id: currentId };
    }

    return (
      stepGroup(layout, at.columnIndex, at.groupIndex, delta) ??
      focusItem(at.group, 0, false) ?? { type: 'back' }
    );
  }

  if (dir === 'left') {
    return layout.trap && at.groupIndex > 0
      ? (stepGroup(layout, at.columnIndex, at.groupIndex, -1) ?? { type: 'back' })
      : { type: 'back' };
  }

  if (dir === 'right') {
    const nextColumn = layout.columns[at.columnIndex + 1];
    if (nextColumn && nextColumn.length > 0) {
      const group = groupInColumn(layout, at.columnIndex + 1, 0);
      if (group) {
        return focusItem(group, 0, false) ?? { type: 'forward', id: currentId };
      }
    }

    if (layout.trap) {
      const next = groupInColumn(layout, at.columnIndex, at.groupIndex + 1);
      return next
        ? (focusItem(next, 0, false) ?? { type: 'forward', id: currentId })
        : { type: 'forward', id: currentId };
    }

    return { type: 'forward', id: currentId };
  }

  return (
    stepGroup(layout, at.columnIndex, at.groupIndex, dir === 'down' ? 1 : -1) ?? {
      type: 'focus',
      id: currentId,
      activate: false,
    }
  );
}

export function startNavProps(
  group: string,
  id: string,
  col: 'main' | 'pane' | 'maps' | 'overlay',
  axis: 'vertical' | 'horizontal' = 'vertical',
  kind: 'list' | 'radio' = 'list',
): Record<string, string> {
  return {
    'data-start-nav': group,
    'data-start-nav-id': id,
    'data-start-nav-col': col,
    'data-start-nav-axis': axis,
    'data-start-nav-kind': kind,
  };
}

export function startNavDir(key: string): StartNavDir | null {
  if (key === 'ArrowUp') {
    return 'up';
  }

  if (key === 'ArrowDown') {
    return 'down';
  }

  if (key === 'ArrowLeft') {
    return 'left';
  }

  if (key === 'ArrowRight') {
    return 'right';
  }

  return null;
}

export function readStartNav(root: ParentNode): StartNavLayout {
  const nodes = [...root.querySelectorAll<HTMLElement>('[data-start-nav]')].filter(
    (el) => !el.hidden && !el.hasAttribute('disabled') && !el.closest('[hidden]'),
  );
  const overlay = nodes.filter((el) => el.dataset.startNavCol === 'overlay');
  const visible = overlay.length > 0 ? overlay : nodes;
  const trap = overlay.length > 0;

  type Draft = {
    id: string;
    axis: StartNavGroup['axis'];
    kind: StartNavGroup['kind'];
    items: string[];
  };
  const groups: Draft[] = [];
  const columnSets = new Map<string, string[]>();

  for (const el of visible) {
    const groupId = el.dataset.startNav;
    const id = el.dataset.startNavId;
    if (!groupId || !id) {
      continue;
    }

    let group = groups.find((entry) => entry.id === groupId);
    if (!group) {
      group = {
        id: groupId,
        axis: el.dataset.startNavAxis === 'horizontal' ? 'horizontal' : 'vertical',
        kind: el.dataset.startNavKind === 'radio' ? 'radio' : 'list',
        items: [],
      };
      groups.push(group);
      const col = el.dataset.startNavCol ?? 'pane';
      const list = columnSets.get(col) ?? [];
      if (!list.includes(groupId)) {
        list.push(groupId);
      }
      columnSets.set(col, list);
    }

    if (!group.items.includes(id)) {
      group.items.push(id);
    }
  }

  const columns = COLUMN_ORDER.map((col) => columnSets.get(col) ?? []).filter(
    (column) => column.length > 0,
  );

  return { columns, groups, trap };
}

export function startNavElement(root: ParentNode, id: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-start-nav-id="${CSS.escape(id)}"]`);
}

export function focusedStartNavId(root: ParentNode): string | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !root.contains(active)) {
    return null;
  }

  return active.dataset.startNavId ?? null;
}

export function focusStartNav(el: HTMLElement): void {
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}
